import React, { useState, useEffect, useCallback, useMemo } from 'react';

const isRealImage = (url) => url && (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:'));

const BLOCKED_PATTERNS = [
  /@/,  // email @ sign
  /\.com\b/i, /\.net\b/i, /\.org\b/i, /\.io\b/i, /\.co\b/i, /\.me\b/i, /\.fr\b/i, /\.de\b/i, /\.uk\b/i,
];
const hasContactInfo = (text) => {
  if (!text) return false;
  return BLOCKED_PATTERNS.some(p => p.test(text));
};

const _DEFAULT_DARIJA_PHRASES = [
  { darija: 'Tbadel m3aya?', arabic: 'تبادل معايا؟', meaning: 'Wanna trade with me?' },
  { darija: 'Bssaha!', arabic: 'بالصحة!', meaning: 'Wear it in good health!' },
  { darija: 'Chhal hada?', arabic: 'شحال هذا؟', meaning: 'How much is this?' },
  { darija: 'Safi, mttaf9in!', arabic: 'صافي، متفاقين!', meaning: 'Deal, we agree!' },
  { darija: 'Hadi 7aja zwina!', arabic: 'هادي حاجة زوينة!', meaning: 'This is a nice thing!' },
  { darija: 'Allah y3tik saha!', arabic: 'الله يعطيك الصحة!', meaning: 'God give you health!' },
  { darija: 'Zwin bezzaf!', arabic: 'زوين بزاف!', meaning: 'Very beautiful!' },
  { darija: 'Rkhis!', arabic: 'رخيص!', meaning: 'Cheap!' },
  { darija: 'Shukran bzaf!', arabic: 'شكرا بزاف!', meaning: 'Thank you very much!' },
  { darija: 'Ghali bezzaf!', arabic: 'غالي بزاف!', meaning: 'Too expensive!' },
];

const _DEFAULT_ACTION_PHRASES = {
  buy:   { darija: 'Shritiha!', arabic: 'شريتيها!', meaning: 'You bought it!' },
  sell:  { darija: 'Tba3tiha!', arabic: 'تبعتيها!', meaning: 'You sold it!' },
  trade: { darija: 'Tbadltiha!', arabic: 'تبدلتيها!', meaning: 'You traded it!' },
  list:  { darija: 'Dkhliha f souk!', arabic: 'دخليها ف السوق!', meaning: 'Put it in the souk!' },
  offer: { darija: 'Sift l3ard!', arabic: 'سيفت العرض!', meaning: 'Sent the offer!' },
};

// Build action phrases from admin settings, falling back to defaults
const _getActionPhrases = (s) => {
  const get = (action, field) => s?.[`action_phrase_${action}_${field}`];
  const result = {};
  for (const action of ['buy', 'sell', 'trade', 'list', 'offer']) {
    result[action] = {
      darija: get(action, 'darija') || _DEFAULT_ACTION_PHRASES[action].darija,
      arabic: get(action, 'arabic') || _DEFAULT_ACTION_PHRASES[action].arabic,
      meaning: get(action, 'meaning') || _DEFAULT_ACTION_PHRASES[action].meaning,
    };
  }
  return result;
};

function ItemImage({ url, size = 'md', className = '' }) {
  const sizeMap = { sm: 'w-9 h-9', md: 'w-11 h-11', lg: 'w-14 h-14', xl: 'w-16 h-16' };
  const textMap = { sm: 'text-lg', md: 'text-xl', lg: 'text-2xl', xl: 'text-3xl' };
  const s = sizeMap[size] || sizeMap.md;
  const t = textMap[size] || textMap.md;
  if (isRealImage(url)) {
    return <img src={url} alt="" className={`${s} rounded-lg object-cover ${className}`} />;
  }
  return <span className={`${t} ${className}`}>{url || '👕'}</span>;
}

export default function MarketplaceScreen({
  ownedItems = [], allItems = [], coins = 0,
  API, userId, gameSettings = {},
  onBack, playSound, onRefreshState, onLearnWord
}) {
  // Configurable text — admin can override all of these
  const s = gameSettings;
  const iconToken = s.icon_token || '🪙';
  const DARIJA_PHRASES = (s.darija_phrases?.length > 0) ? s.darija_phrases : _DEFAULT_DARIJA_PHRASES;
  const ACTION_PHRASES = _getActionPhrases(s);

  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overlay, setOverlay] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);

  const [listItem, setListItem] = useState(null);
  const [listWantedDesc, setListWantedDesc] = useState('');
  const [listAcceptItems, setListAcceptItems] = useState(true);
  const [listTokenPrice, setListTokenPrice] = useState('');

  const [selectedListing, setSelectedListing] = useState(null);
  const [offerItemId, setOfferItemId] = useState(null);
  const [offerSending, setOfferSending] = useState(false);
  const [offerResult, setOfferResult] = useState(null);

  const [buyListingData, setBuyListingData] = useState(null);
  const [buying, setBuying] = useState(false);
  const [buyResult, setBuyResult] = useState(null);

  const [respondingId, setRespondingId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [listingPending, setListingPending] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [listingSuccess, setListingSuccess] = useState(null);
  const [confirmCancelListing, setConfirmCancelListing] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [editListing, setEditListing] = useState(null);
  const [editWantedDesc, setEditWantedDesc] = useState('');
  const [editAcceptItems, setEditAcceptItems] = useState(true);
  const [editTokenPrice, setEditTokenPrice] = useState('');
  const [editPending, setEditPending] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('user_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }, []);

  const itemMap = useMemo(() => {
    const map = {};
    allItems.forEach(i => { map[i.id] = i; });
    return map;
  }, [allItems]);

  const fetchListings = useCallback(async () => {
    if (!API) return;
    setLoading(true);
    try {
      const [listingsRes, mineRes] = await Promise.all([
        fetch(`${API}/souk-clothes/trade-listings`, { headers }),
        fetch(`${API}/souk-clothes/trade-listings/mine`, { headers })
      ]);
      if (listingsRes.ok) setListings(await listingsRes.json());
      if (mineRes.ok) setMyListings(await mineRes.json());
    } catch (err) { console.error('Marketplace fetch error:', err); }
    setLoading(false);
  }, [API, headers]);

  const fetchNotifications = useCallback(async () => {
    if (!API || !userId) return;
    try {
      const res = await fetch(`${API}/souk-clothes/trade-notifications/${userId}`, { headers });
      if (res.ok) setNotifications(await res.json());
    } catch (err) { console.error('Notif fetch error:', err); }
  }, [API, userId, headers]);

  const fetchNotifCount = useCallback(async () => {
    if (!API || !userId) return;
    try {
      const res = await fetch(`${API}/souk-clothes/trade-notifications/${userId}/count`, { headers });
      if (res.ok) {
        const data = await res.json();
        setNotifCount(data.unread_count || 0);
      }
    } catch (err) { console.error('Notif count error:', err); }
  }, [API, userId, headers]);

  useEffect(() => {
    fetchListings();
    fetchNotifCount();
  }, [fetchListings, fetchNotifCount]);

  const handleCreateListing = async () => {
    if (!listItem || !API) return;
    const tokenPrice = parseInt(listTokenPrice) || 0;
    if (!listAcceptItems && tokenPrice <= 0) {
      alert(s.mp_validation_trade_or_price || 'Must accept item trades or set a token price');
      return;
    }
    setListingPending(true);
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings`, {
        method: 'POST', headers,
        body: JSON.stringify({
          item_id: listItem.id,
          wanted_description: listWantedDesc,
          accept_items: listAcceptItems,
          token_price: tokenPrice
        })
      });
      if (res.ok) {
        const newListing = await res.json();
        playSound?.('success');
        setListingSuccess(listItem);
        setOverlay('listing-success');
        // Instantly add to myListings so it appears immediately
        setMyListings(prev => [newListing, ...prev]);
        fetchListings();
        // Learn list phrase
        if (onLearnWord) onLearnWord(ACTION_PHRASES.list.darija, ACTION_PHRASES.list.meaning, ACTION_PHRASES.list.arabic, 'phrase');
        setListItem(null);
        setListWantedDesc('');
        setListAcceptItems(true);
        setListTokenPrice('');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      } else {
        const err = await res.json();
        alert(err.detail || s.mp_error_create_listing || 'Failed to create listing');
      }
    } catch (err) { console.error('Create listing error:', err); }
    setListingPending(false);
  };

  const handleSendOffer = async () => {
    if (!selectedListing || !offerItemId || !API) return;
    setOfferSending(true);
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings/${selectedListing.id}/offer`, {
        method: 'POST', headers,
        body: JSON.stringify({ offer_item_id: offerItemId })
      });
      if (res.ok) {
        playSound?.('success');
        setOfferResult({ success: true });
        fetchListings();
        // Learn offer phrase
        if (onLearnWord) onLearnWord(ACTION_PHRASES.offer.darija, ACTION_PHRASES.offer.meaning, ACTION_PHRASES.offer.arabic, 'phrase');
      } else {
        const err = await res.json();
        alert(err.detail || s.mp_error_send_offer || 'Failed to send offer');
      }
    } catch (err) { console.error('Send offer error:', err); }
    setOfferSending(false);
  };

  const handleBuyListing = async () => {
    if (!buyListingData || !API) return;
    setBuying(true);
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings/${buyListingData.id}/buy`, {
        method: 'POST', headers
      });
      if (res.ok) {
        const data = await res.json();
        playSound?.('coin');
        setBuyResult(data);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        onRefreshState?.();
        fetchListings();
        // Learn buy phrase + clothing word
        if (onLearnWord) {
          onLearnWord(ACTION_PHRASES.buy.darija, ACTION_PHRASES.buy.meaning, ACTION_PHRASES.buy.arabic, 'phrase');
          if (buyListingData.item_darija) onLearnWord(buyListingData.item_darija, buyListingData.item_name, buyListingData.item_arabic || '', 'clothing');
        }
      } else {
        const err = await res.json();
        alert(err.detail || s.mp_error_buy || 'Failed to buy');
      }
    } catch (err) { console.error('Buy error:', err); }
    setBuying(false);
  };

  const handleRespond = async (offerId, action) => {
    if (!API) return;
    setRespondingId(offerId);
    try {
      const res = await fetch(`${API}/souk-clothes/trade-offers/${offerId}/respond`, {
        method: 'POST', headers,
        body: JSON.stringify({ action })
      });
      if (res.ok) {
        playSound?.(action === 'accept' ? 'success' : 'tick');
        if (action === 'accept') {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
          onRefreshState?.();
        }
        fetchNotifications();
        fetchListings();
        fetchNotifCount();
      } else {
        const err = await res.json();
        alert(err.detail || s.mp_error_respond || 'Failed to respond');
      }
    } catch (err) { console.error('Respond error:', err); }
    setRespondingId(null);
  };

  const handleMarkRead = async () => {
    if (!API || !userId) return;
    try {
      await fetch(`${API}/souk-clothes/trade-notifications/${userId}/read`, { method: 'POST', headers });
      setNotifCount(0);
    } catch (err) { console.error('Mark read error:', err); }
  };

  const handleCancelListing = async (listing) => {
    if (!API) return;
    setCancellingId(listing.id);
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings/${listing.id}`, { method: 'DELETE', headers });
      if (res.ok) { playSound?.('tick'); fetchListings(); }
      else { const err = await res.json(); alert(err.detail || 'Failed'); }
    } catch (err) { console.error(err); }
    setCancellingId(null);
    setConfirmCancelListing(null);
  };

  const handleEditListing = async () => {
    if (!editListing || !API) return;
    const tokenPrice = parseInt(editTokenPrice) || 0;
    if (!editAcceptItems && tokenPrice <= 0) {
      alert(s.mp_validation_trade_or_price || 'Must accept item trades or set a token price');
      return;
    }
    if (hasContactInfo(editWantedDesc)) {
      alert(s.mp_no_contact_info || 'No emails, phone numbers, or social media allowed');
      return;
    }
    setEditPending(true);
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings/${editListing.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          wanted_description: editWantedDesc,
          accept_items: editAcceptItems,
          token_price: tokenPrice
        })
      });
      if (res.ok) {
        playSound?.('success');
        setOverlay(null);
        setEditListing(null);
        fetchListings();
      } else {
        const err = await res.json();
        alert(err.detail || s.mp_error_edit_listing || 'Failed to update listing');
      }
    } catch (err) { console.error('Edit listing error:', err); }
    setEditPending(false);
  };

  const dayIdx = Math.floor(Date.now() / 86400000) % DARIJA_PHRASES.length;
  const phrase = DARIJA_PHRASES[dayIdx];

  // Auto-learn daily phrase on marketplace open
  useEffect(() => {
    if (phrase && onLearnWord) {
      onLearnWord(phrase.darija, phrase.meaning, phrase.arabic || '', 'phrase');
    }
  }, [dayIdx]); // eslint-disable-line

  const myListedItemIds = new Set(myListings.map(l => l.item_id));

  const CATEGORIES = [
    { id: 'all', label: s.mp_cat_all || 'All', icon: '🏪' },
    { id: 'tops', label: s.mp_cat_tops || 'Tops', icon: '👕' },
    { id: 'bottoms', label: s.mp_cat_bottoms || 'Bottoms', icon: '👖' },
    { id: 'shoes', label: s.mp_cat_shoes || 'Shoes', icon: '👟' },
    { id: 'accessories', label: s.mp_cat_accessories || 'Accessories', icon: '🎒' },
    { id: 'outerwear', label: s.mp_cat_outerwear || 'Outerwear', icon: '🧥' },
  ];

  const filteredListings = useMemo(() => {
    let result = [...listings];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l => (l.item_name || '').toLowerCase().includes(q));
    }
    if (activeCategory !== 'all') {
      result = result.filter(l => (l.item_category || '').toLowerCase().includes(activeCategory));
    }
    if (sortBy === 'cheapest') {
      result.sort((a, b) => (a.token_price || 999999) - (b.token_price || 999999));
    } else if (sortBy === 'popular') {
      result.sort((a, b) => (b.offer_count || 0) - (a.offer_count || 0));
    }
    return result;
  }, [listings, searchQuery, activeCategory, sortBy]);

  return (
    <div className="min-h-screen bg-[#F5F0E8]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {s.mp_back_button || 'Back'}
          </button>
          <h1 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
            {s.icon_marketplace || '🏪'} {s.mp_title || 'Marketplace'}
          </h1>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
              {iconToken} {coins}
            </span>
            <button
              onClick={() => { setOverlay('notifications'); fetchNotifications(); handleMarkRead(); playSound?.('tick'); }}
              className="relative p-1.5"
            >
              <span className="text-lg">{s.icon_notifications || '🔔'}</span>
              {notifCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
                  {notifCount > 9 ? '9+' : notifCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Search + Filters (sticky below header) */}
      <div className="bg-white border-b border-gray-100 sticky top-[52px] z-30">
        <div className="max-w-4xl mx-auto px-4 py-2.5">
          {/* Search bar */}
          <div className="relative mb-2">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={s.mp_search_placeholder || 'Search items...'}
              className="w-full pl-9 pr-3 py-2 rounded-full bg-gray-100 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30 focus:bg-white transition-all"
            />
          </div>
          {/* Category pills */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); playSound?.('tick'); }}
                className={`px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#2D6A4F] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pr-14 py-3">
        {/* Darija Word of the Day */}
        {phrase && (
          <div
            className="mb-3 p-3 rounded-xl bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200/60 cursor-pointer"
            onClick={() => { if (onLearnWord) onLearnWord(phrase.darija, phrase.meaning, phrase.arabic || '', 'phrase'); }}
          >
            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">🇲🇦 {s.mp_word_of_day_label || 'Word of the Day'}</p>
            <p className="text-base font-bold text-gray-900">{phrase.darija}</p>
            {phrase.arabic && <p className="text-sm text-gray-600 mt-0.5" dir="rtl">{phrase.arabic}</p>}
            <p className="text-xs text-amber-700 italic mt-0.5">{phrase.meaning}</p>
          </div>
        )}

        {/* Sell button row */}
        <div className="flex items-center justify-end mb-3">
          <button
            onClick={() => { setListItem(null); setOverlay('list-item'); playSound?.('tick'); }}
            className="flex-shrink-0 px-3.5 py-2 rounded-full bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-xs transition-colors shadow-sm"
          >
            {s.mp_list_button || '+ Sell'}
          </button>
        </div>

        {/* Sort row */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500">
            {filteredListings.length} {filteredListings.length !== 1 ? (s.mp_items_label || 'items') : (s.mp_item_label || 'item')}
          </p>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="text-xs text-gray-600 bg-transparent border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#2D6A4F]"
          >
            <option value="newest">{s.mp_sort_newest || 'Newest'}</option>
            <option value="cheapest">{s.mp_sort_cheapest || 'Cheapest'}</option>
            <option value="popular">{s.mp_sort_popular || 'Most Popular'}</option>
          </select>
        </div>

        {/* Your Listings — same grid as marketplace */}
        {myListings.length > 0 && (
          <div className="mb-4" id="mp-your-listings">
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">{s.mp_your_listings_title || 'Your Listings'}</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {myListings.map(l => (
                <div key={l.id} className="bg-green-50 rounded-xl border border-green-200 shadow-sm overflow-hidden relative group">
                  {/* Edit/Cancel buttons */}
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <button
                      onClick={() => {
                        setEditListing(l);
                        setEditWantedDesc(l.wanted_description || '');
                        setEditAcceptItems(l.accept_items !== false);
                        setEditTokenPrice(String(l.token_price || ''));
                        setOverlay('edit-listing');
                        playSound?.('tick');
                      }}
                      className="w-7 h-7 rounded-full bg-blue-100/90 text-blue-600 text-xs flex items-center justify-center hover:bg-blue-200 shadow-sm"
                      title={s.mp_edit_button || 'Edit'}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => { setConfirmCancelListing(l); playSound?.('tick'); }}
                      className="w-7 h-7 rounded-full bg-red-100/90 text-red-500 text-xs flex items-center justify-center hover:bg-red-200 shadow-sm"
                      title={s.mp_cancel_listing_button || 'Cancel'}
                    >
                      ✕
                    </button>
                  </div>
                  {/* Image area — same as grid cards */}
                  <div className="relative aspect-square bg-white flex items-center justify-center overflow-hidden">
                    {isRealImage(l.item_image) ? (
                      <img src={l.item_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-5xl">{l.item_image || '👕'}</span>
                    )}
                  </div>
                  {/* Info area */}
                  <div className="p-2.5">
                    <p className="text-xs font-bold text-gray-800 truncate leading-tight">{l.item_name}</p>
                    {l.item_darija && (
                      <p className="text-[9px] text-emerald-600 truncate leading-tight">{l.item_darija}{l.item_arabic ? ` • ${l.item_arabic}` : ''}</p>
                    )}
                    <p className="text-xs font-bold text-amber-700 mt-0.5">
                      {(l.token_price || 0) > 0 ? `${iconToken} ${l.token_price}` : (s.mp_trade_only_label || '🤝 Trade')}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">🤝 {l.offer_count || 0} {(l.offer_count || 0) !== 1 ? (s.mp_offers_plural || 'offers') : (s.mp_offer_singular || 'offer')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Listings — 2-column grid (Facebook Marketplace style) */}
        {loading ? (
          <div className="text-center py-16">
            <span className="text-4xl block mb-3 animate-spin">🔄</span>
            <p className="text-gray-400 text-sm">{s.mp_loading || 'Loading marketplace...'}</p>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="text-center py-16">
            <span className="text-6xl block mb-4">{s.icon_marketplace || '🏪'}</span>
            {myListings.length > 0 ? (
              <>
                <p className="text-gray-600 text-base font-bold mb-1">{s.mp_no_others_title || 'No other listings yet'}</p>
                <p className="text-gray-400 text-xs">{s.mp_no_others_desc || 'Check back soon for new items from other traders!'}</p>
              </>
            ) : (
              <>
                <p className="text-gray-600 text-base font-bold mb-1">{s.mp_empty_title || 'No listings yet'}</p>
                <p className="text-gray-400 text-xs mb-5">{s.mp_empty_desc || 'Be the first to list an item!'}</p>
                <button
                  onClick={() => { setListItem(null); setOverlay('list-item'); playSound?.('tick'); }}
                  className="px-6 py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
                >
                  {s.mp_list_button || '+ Sell'}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            {filteredListings.map(l => {
              const tokenPrice = l.token_price || 0;
              const offers = l.offer_count || 0;
              return (
                <button
                  key={l.id}
                  onClick={() => {
                    setSelectedListing(l); setOverlay('item-detail'); playSound?.('tick');
                    if (l.item_darija && onLearnWord) onLearnWord(l.item_darija, l.item_name, l.item_arabic || '', 'clothing');
                  }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md hover:border-gray-300 transition-all text-left active:scale-[0.97] group"
                >
                  {/* Image area */}
                  <div className="relative aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                    {isRealImage(l.item_image) ? (
                      <img src={l.item_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <span className="text-5xl">{l.item_image || '👕'}</span>
                    )}
                    {(l.view_count || 0) >= 5 && (
                      <span className="absolute top-1.5 left-1.5 bg-orange-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">{s.mp_hot_badge || 'HOT'}</span>
                    )}
                    {offers > 0 && (
                      <span className="absolute top-1.5 right-1.5 bg-blue-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">🤝 {offers}</span>
                    )}
                  </div>
                  {/* Info area */}
                  <div className="p-2.5">
                    <p className="text-xs font-bold text-gray-800 truncate leading-tight">{l.item_name}</p>
                    {l.item_darija && (
                      <p className="text-[9px] text-emerald-600 truncate leading-tight">{l.item_darija}{l.item_arabic ? ` • ${l.item_arabic}` : ''}</p>
                    )}
                    <p className="text-xs font-bold text-amber-700 mt-0.5">
                      {tokenPrice > 0 ? `${iconToken} ${tokenPrice}` : (s.mp_trade_only_label || '🤝 Trade')}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">{l.anonymous_name || s.mp_default_trader || 'Trader'}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ OVERLAY: Listing Success ═══ */}
      {overlay === 'listing-success' && listingSuccess && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-up">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <span className="text-5xl block mb-3">🎉</span>
            <p className="text-lg font-bold text-gray-900 mb-1">{s.mp_listed_title || 'Listed!'}</p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-2">
              <p className="text-sm font-bold text-emerald-700">{ACTION_PHRASES.list.darija}</p>
              <p className="text-[10px] text-emerald-500">{ACTION_PHRASES.list.meaning} • {ACTION_PHRASES.list.arabic}</p>
            </div>
            <p className="text-sm text-gray-500 mb-1">
              {listingSuccess.name_english} {s.mp_listed_desc || 'is now on the marketplace'}
            </p>
            <p className="text-xs text-gray-400 mb-5">{s.mp_listed_note || 'Other traders can now see and offer for your item'}</p>
            <button
              onClick={() => { setOverlay(null); setListingSuccess(null); }}
              className="w-full py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
            >
              {s.mp_back_to_marketplace || 'Back to Marketplace'}
            </button>
          </div>
        </div>
      )}

      {/* ═══ OVERLAY: Item Detail ═══ */}
      {overlay === 'item-detail' && selectedListing && (() => {
        const l = selectedListing;
        const acceptsItems = l.accept_items !== false;
        const tokenPrice = l.token_price || 0;
        const offers = l.offer_count || 0;
        const views = l.view_count || 0;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center animate-fade-up">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
                <h3 className="text-base font-bold text-gray-900 truncate pr-2">{l.item_name}</h3>
                <button onClick={() => { setOverlay(null); setSelectedListing(null); }} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 text-gray-600 flex-shrink-0">✕</button>
              </div>
              <div className="p-5">
                {/* Item preview */}
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-center mb-4">
                  <div className="w-20 h-20 mx-auto rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden mb-3">
                    <ItemImage url={l.item_image} size="xl" />
                  </div>
                  <p className="text-base font-bold text-gray-900">{l.item_name}</p>
                  {l.item_darija && (
                    <p className="text-sm text-emerald-600 font-medium mt-0.5">{l.item_darija}{l.item_arabic ? ` • ${l.item_arabic}` : ''}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5">{s.mp_by_label || 'by'} {l.anonymous_name || s.mp_default_trader || 'Trader'}</p>
                  {(views > 0 || offers > 0) && (
                    <div className="flex items-center justify-center gap-3 mt-2">
                      <span className="text-[10px] text-gray-400">👀 {views} {s.mp_views_label || 'views'}</span>
                      <span className="text-[10px] text-gray-400">🤝 {offers} {offers !== 1 ? (s.mp_offers_plural || 'offers') : (s.mp_offer_singular || 'offer')}</span>
                    </div>
                  )}
                </div>

                {/* What seller wants */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-1">{s.mp_seller_wants || 'Seller wants'}</p>
                  <div className="flex flex-wrap gap-2">
                    {acceptsItems && (
                      <span className="text-xs bg-blue-100 text-blue-800 font-semibold px-2 py-1 rounded-lg">{s.mp_accepts_items || '🤝 Accepts item trades'}</span>
                    )}
                    {tokenPrice > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2 py-1 rounded-lg">{iconToken} {tokenPrice} {s.mp_tokens_label || 'tokens'}</span>
                    )}
                  </div>
                  {l.wanted_description && (
                    <p className="text-xs text-amber-700 mt-2">"{l.wanted_description}"</p>
                  )}
                </div>

                {/* Activity info */}
                {offers > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                    <p className="text-xs text-blue-700 font-semibold">
                      🤝 {offers} {offers !== 1 ? (s.mp_people_interested || 'people already interested') : (s.mp_person_interested || 'person already interested')}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="space-y-2.5">
                  {acceptsItems && (
                    <button
                      onClick={() => { setOfferItemId(null); setOfferResult(null); setOverlay('make-offer'); playSound?.('tick'); }}
                      className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
                    >
                      {s.mp_trade_my_item || '🤝 Trade My Item'}
                    </button>
                  )}
                  {tokenPrice > 0 && (
                    <button
                      onClick={() => { setBuyListingData(l); setBuyResult(null); setOverlay('buy-confirm'); playSound?.('tick'); }}
                      className="w-full py-3.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition-colors"
                    >
                      {s.mp_buy_for_label || 'Buy for'} {iconToken} {tokenPrice}
                    </button>
                  )}
                  <button
                    onClick={() => { setOverlay(null); setSelectedListing(null); }}
                    className="w-full py-2.5 text-gray-400 text-sm hover:text-gray-600 transition-colors"
                  >
                    {s.mp_close_button || 'Close'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ OVERLAY: List an Item ═══ */}
      {overlay === 'list-item' && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center animate-fade-up">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="text-base font-bold text-gray-900">
                {listItem ? (s.mp_set_trade_options || 'Set Trade Options') : (s.mp_pick_item_title || 'Pick an Item to List')}
              </h3>
              <button onClick={() => { setOverlay(null); setListItem(null); }} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 text-gray-600">✕</button>
            </div>
            <div className="p-5">
              {!listItem ? (
                <div className="grid grid-cols-3 gap-2.5 max-h-[50vh] overflow-y-auto">
                  {ownedItems.filter(id => {
                    const item = itemMap[id];
                    return item && !item.is_starter && !item.is_free;
                  }).map(id => {
                    const item = itemMap[id];
                    const alreadyListed = myListedItemIds.has(id);
                    return (
                      <button
                        key={id}
                        disabled={alreadyListed}
                        onClick={() => { setListItem(item); playSound?.('tick'); }}
                        className={`rounded-xl border-2 p-2.5 text-center transition-all active:scale-95 ${
                          alreadyListed ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed' : 'border-gray-100 bg-gray-50 hover:border-[#D4A843] hover:shadow-md'
                        }`}
                      >
                        <ItemImage url={item?.image_url} size="sm" className="mx-auto mb-1" />
                        <p className="text-[9px] font-bold text-gray-700 leading-tight line-clamp-2">{item?.name_english}</p>
                        {item?.name_darija && <p className="text-[7px] text-emerald-600 truncate">{item.name_darija}</p>}
                        <p className="text-[9px] text-gray-400">{iconToken} {item?.base_price_tokens || 0}</p>
                        {alreadyListed && <p className="text-[8px] text-amber-500 font-bold">{s.mp_already_listed || 'Listed'}</p>}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-center">
                    <ItemImage url={listItem.image_url} size="lg" className="mx-auto mb-2" />
                    <p className="text-sm font-bold text-gray-800">{listItem.name_english}</p>
                    {listItem.name_darija && <p className="text-xs text-emerald-600 font-medium">{listItem.name_darija}{listItem.name_arabic ? ` • ${listItem.name_arabic}` : ''}</p>}
                    <p className="text-xs text-amber-600">{iconToken} {listItem.base_price_tokens || 0}</p>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs font-bold text-gray-600 block mb-2">{s.mp_how_to_trade || 'How do you want to trade?'}</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-[#D4A843] transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={listAcceptItems}
                          onChange={e => setListAcceptItems(e.target.checked)}
                          className="w-4 h-4 rounded text-[#2D6A4F] focus:ring-[#2D6A4F]"
                        />
                        <div>
                          <p className="text-xs font-bold text-gray-700">{s.mp_accept_items_label || '🤝 Accept item trades'}</p>
                          <p className="text-[10px] text-gray-400">{s.mp_accept_items_desc || 'Other players can offer their items'}</p>
                        </div>
                      </label>
                      <div className="p-3 rounded-xl border-2 border-gray-200">
                        <div className="flex items-center gap-3">
                          <span className="text-sm">{iconToken}</span>
                          <div className="flex-1">
                            <p className="text-xs font-bold text-gray-700">{s.mp_token_price_label || 'Set token price (optional)'}</p>
                            <input
                              type="number"
                              value={listTokenPrice}
                              onChange={e => setListTokenPrice(e.target.value)}
                              placeholder="0"
                              min="0"
                              className="w-full mt-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-[#D4A843] focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs font-bold text-gray-600 block mb-1.5">{s.mp_want_label || 'What do you want in return? (optional)'}</label>
                    <input
                      type="text"
                      value={listWantedDesc}
                      onChange={e => setListWantedDesc(e.target.value)}
                      placeholder={s.mp_want_placeholder || 'e.g. jacket, shoes, accessories...'}
                      className={`w-full px-3 py-2.5 rounded-xl border-2 text-sm focus:outline-none transition-colors ${
                        hasContactInfo(listWantedDesc) ? 'border-red-300 focus:border-red-400 bg-red-50' : 'border-gray-200 focus:border-[#D4A843]'
                      }`}
                      maxLength={100}
                    />
                    {hasContactInfo(listWantedDesc) && (
                      <p className="text-[10px] text-red-500 font-semibold mt-1">
                        {s.mp_no_contact_info || '⚠️ No emails, phone numbers, or social media allowed — this is a game!'}
                      </p>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-xl p-3 mb-4 text-center">
                    <p className="text-[10px] text-gray-500">{s.mp_anonymous_note || 'Your listing will be anonymous — other traders see your marketplace name only'}</p>
                  </div>

                  <div className="space-y-2.5">
                    <button
                      onClick={handleCreateListing}
                      disabled={listingPending || hasContactInfo(listWantedDesc) || (!listAcceptItems && (parseInt(listTokenPrice) || 0) <= 0)}
                      className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      {listingPending ? (s.mp_listing_pending || 'Listing...') : (s.mp_confirm_list || 'List for Trade')}
                    </button>
                    <button
                      onClick={() => setListItem(null)}
                      className="w-full py-2.5 text-gray-400 text-sm hover:text-gray-600 transition-colors"
                    >
                      {s.mp_pick_different || '← Pick Different Item'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ OVERLAY: Make Offer ═══ */}
      {overlay === 'make-offer' && selectedListing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center animate-fade-up">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="text-base font-bold text-gray-900">{s.mp_send_offer_title || 'Send Trade Offer'}</h3>
              <button onClick={() => { setOverlay(null); setOfferResult(null); }} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 text-gray-600">✕</button>
            </div>
            <div className="p-5">
              {offerResult?.success ? (
                <div className="text-center py-6">
                  <span className="text-5xl block mb-3">🎉</span>
                  <p className="text-lg font-bold text-gray-900 mb-1">{s.mp_offer_sent_title || 'Offer Sent!'}</p>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-2">
                    <p className="text-sm font-bold text-emerald-700">{ACTION_PHRASES.offer.darija}</p>
                    <p className="text-[10px] text-emerald-500">{ACTION_PHRASES.offer.meaning} • {ACTION_PHRASES.offer.arabic}</p>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">
                    {selectedListing.anonymous_name || s.mp_default_trader || 'The trader'} {s.mp_offer_sent_will_notify || 'will be notified.'}
                  </p>
                  <p className="text-xs text-gray-400 mb-5">{s.mp_offer_sent_desc || "They can accept or decline. You'll get a notification either way!"}</p>
                  <button
                    onClick={() => { setOverlay(null); setOfferResult(null); fetchListings(); }}
                    className="w-full py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
                  >
                    {s.mp_back_to_marketplace || 'Back to Marketplace'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
                    <p className="text-[10px] text-green-600 font-bold mb-2">{s.mp_theyre_trading || "They're trading:"}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-white border border-green-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        <ItemImage url={selectedListing.item_image} size="md" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-800">{selectedListing.item_name}</p>
                        <p className="text-[10px] text-gray-500">{selectedListing.anonymous_name || s.mp_default_trader || 'Trader'}</p>
                      </div>
                    </div>
                    {selectedListing.wanted_description && (
                      <p className="text-[10px] text-amber-600 mt-2">{s.mp_looking_for || 'Looking for'}: {selectedListing.wanted_description}</p>
                    )}
                  </div>

                  <p className="text-xs font-bold text-gray-600 mb-2">{s.mp_pick_offer_item || 'Pick your item to offer:'}</p>
                  <div className="grid grid-cols-3 gap-2.5 max-h-[30vh] overflow-y-auto mb-4">
                    {ownedItems.filter(id => {
                      const item = itemMap[id];
                      return item && !item.is_starter && !item.is_free;
                    }).map(id => {
                      const item = itemMap[id];
                      const isSelected = offerItemId === id;
                      return (
                        <button
                          key={id}
                          onClick={() => { setOfferItemId(id); playSound?.('tick'); }}
                          className={`rounded-xl border-2 p-2.5 text-center transition-all active:scale-95 ${
                            isSelected ? 'border-[#2D6A4F] bg-green-50 shadow-md' : 'border-gray-100 bg-gray-50 hover:border-[#D4A843]'
                          }`}
                        >
                          <ItemImage url={item?.image_url} size="sm" className="mx-auto mb-1" />
                          <p className="text-[9px] font-bold text-gray-700 leading-tight line-clamp-2">{item?.name_english}</p>
                        </button>
                      );
                    })}
                  </div>

                  {offerItemId && (() => {
                    const offerItem = itemMap[offerItemId];
                    return (
                      <div className="bg-gray-50 rounded-xl p-3 mb-4">
                        <div className="flex items-center justify-center gap-3">
                          <div className="text-center">
                            <p className="text-[10px] text-red-500 font-bold mb-1">{s.mp_you_give || 'YOU GIVE'}</p>
                            <ItemImage url={offerItem?.image_url} size="sm" className="mx-auto" />
                            <p className="text-[9px] font-bold text-gray-700 mt-1 max-w-[80px] truncate">{offerItem?.name_english}</p>
                          </div>
                          <span className="text-xl text-gray-400">🔄</span>
                          <div className="text-center">
                            <p className="text-[10px] text-green-600 font-bold mb-1">{s.mp_you_get || 'YOU GET'}</p>
                            <ItemImage url={selectedListing.item_image} size="sm" className="mx-auto" />
                            <p className="text-[9px] font-bold text-gray-700 mt-1 max-w-[80px] truncate">{selectedListing.item_name}</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-center text-green-600 font-semibold mt-2">{s.mp_free_trade_note || 'Free trade — no fees!'}</p>
                      </div>
                    );
                  })()}

                  <button
                    onClick={handleSendOffer}
                    disabled={!offerItemId || offerSending}
                    className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                  >
                    {offerSending ? (s.mp_sending || 'Sending...') : (s.mp_send_offer_button || '🤝 Send Trade Offer')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ OVERLAY: Buy Confirm ═══ */}
      {overlay === 'buy-confirm' && buyListingData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-up">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full">
            <div className="p-6 text-center">
              {buyResult ? (
                <>
                  <span className="text-5xl block mb-3">🎉</span>
                  <p className="text-lg font-bold text-gray-900 mb-1">{s.mp_purchased_title || 'Purchased!'}</p>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-2">
                    <p className="text-sm font-bold text-emerald-700">{ACTION_PHRASES.buy.darija}</p>
                    <p className="text-[10px] text-emerald-500">{ACTION_PHRASES.buy.meaning} • {ACTION_PHRASES.buy.arabic}</p>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">{s.mp_you_got || 'You got'} {buyResult.item_name}!</p>
                  <p className="text-xs text-gray-400 mb-5">{s.mp_paid_label || 'Paid'} {iconToken} {buyResult.price}</p>
                  <button
                    onClick={() => { setOverlay(null); setBuyListingData(null); setBuyResult(null); fetchListings(); }}
                    className="w-full py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
                  >
                    {s.mp_back_to_marketplace || 'Back to Marketplace'}
                  </button>
                </>
              ) : (
                <>
                  <span className="text-4xl block mb-3">{iconToken}</span>
                  <p className="text-lg font-bold text-gray-900 mb-2">{s.mp_buy_confirm_title || 'Buy'} {buyListingData.item_name}?</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-white border border-amber-200 flex items-center justify-center overflow-hidden">
                        <ItemImage url={buyListingData.item_image} size="md" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-800">{buyListingData.item_name}</p>
                        <p className="text-xs text-gray-500">{s.mp_from_label || 'from'} {buyListingData.anonymous_name || s.mp_default_trader || 'Trader'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{s.mp_price_label || 'Price'}</span>
                      <span className="font-bold text-amber-700">{iconToken} {buyListingData.token_price}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-500">{s.mp_balance_label || 'Your balance'}</span>
                      <span className={`font-bold ${coins >= buyListingData.token_price ? 'text-green-600' : 'text-red-500'}`}>
                        {iconToken} {coins}
                      </span>
                    </div>
                    {coins < buyListingData.token_price && (
                      <p className="text-xs text-red-500 font-semibold mt-2">{s.mp_not_enough_tokens || 'Not enough tokens!'}</p>
                    )}
                  </div>
                  <div className="space-y-2.5">
                    <button
                      onClick={handleBuyListing}
                      disabled={buying || coins < buyListingData.token_price}
                      className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      {buying ? (s.mp_buying || 'Buying...') : `${s.mp_buy_for_label || 'Buy for'} ${iconToken} ${buyListingData.token_price}`}
                    </button>
                    <button
                      onClick={() => { setOverlay(null); setBuyListingData(null); }}
                      className="w-full py-2.5 text-gray-400 text-sm hover:text-gray-600 transition-colors"
                    >
                      {s.mp_cancel_button || 'Cancel'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ OVERLAY: Notifications ═══ */}
      {overlay === 'notifications' && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center animate-fade-up">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="text-base font-bold text-gray-900">{s.icon_notifications || '🔔'} {s.mp_notifications_title || 'Notifications'}</h3>
              <button onClick={() => setOverlay(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 text-gray-600">✕</button>
            </div>
            <div className="p-5">
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-4xl block mb-3">{s.icon_notifications || '🔔'}</span>
                  <p className="text-gray-400 text-sm">{s.mp_no_notif_title || 'No notifications yet'}</p>
                  <p className="text-gray-300 text-xs mt-1">{s.mp_no_notif_desc || "You'll see trade offers and updates here"}</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {notifications.map(n => (
                    <div key={n.id} className={`rounded-xl p-3 border ${
                      n.type === 'trade_offer' ? 'bg-green-50 border-green-200' :
                      n.type === 'offer_accepted' || n.type === 'item_sold' ? 'bg-amber-50 border-amber-200' :
                      n.type === 'offer_declined' ? 'bg-gray-50 border-gray-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        <span className="text-lg mt-0.5">
                          {n.type === 'trade_offer' ? '🤝' :
                           n.type === 'offer_accepted' ? '🎉' :
                           n.type === 'item_sold' ? '💰' :
                           n.type === 'offer_declined' ? '❌' : '📬'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-800">{n.data?.message || n.message || s.mp_default_notif || 'Trade notification'}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                          </p>

                          {n.type === 'trade_offer' && n.data?.offer_id && (
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => handleRespond(n.data.offer_id, 'accept')}
                                disabled={respondingId === n.data.offer_id}
                                className="flex-1 py-1.5 rounded-lg bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-[10px] transition-colors disabled:bg-gray-300"
                              >
                                {respondingId === n.data.offer_id ? '...' : (s.mp_accept_button || 'Accept')}
                              </button>
                              <button
                                onClick={() => handleRespond(n.data.offer_id, 'decline')}
                                disabled={respondingId === n.data.offer_id}
                                className="flex-1 py-1.5 rounded-lg border-2 border-red-200 text-red-600 font-bold text-[10px] hover:bg-red-50 transition-colors disabled:border-gray-200 disabled:text-gray-400"
                              >
                                {respondingId === n.data.offer_id ? '...' : (s.mp_decline_button || 'Decline')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ OVERLAY: Cancel Listing Confirm ═══ */}
      {confirmCancelListing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-up">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <span className="text-4xl block mb-3">🗑️</span>
            <p className="text-base font-bold text-gray-900 mb-1">{s.mp_cancel_confirm_title || 'Cancel Listing?'}</p>
            <p className="text-sm text-gray-500 mb-1">{confirmCancelListing.item_name}</p>
            <p className="text-xs text-gray-400 mb-5">{s.mp_cancel_confirm_desc || 'This will remove your item from the marketplace. You can list it again anytime.'}</p>
            <div className="space-y-2.5">
              <button
                onClick={() => handleCancelListing(confirmCancelListing)}
                disabled={cancellingId === confirmCancelListing.id}
                className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-colors disabled:bg-gray-300"
              >
                {cancellingId === confirmCancelListing.id ? (s.mp_cancelling || 'Cancelling...') : (s.mp_cancel_confirm_button || 'Yes, Cancel Listing')}
              </button>
              <button
                onClick={() => setConfirmCancelListing(null)}
                className="w-full py-2.5 text-gray-400 text-sm hover:text-gray-600 transition-colors"
              >
                {s.mp_keep_listing || 'Keep Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ OVERLAY: Edit Listing ═══ */}
      {overlay === 'edit-listing' && editListing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center animate-fade-up">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="text-base font-bold text-gray-900">{s.mp_edit_listing_title || 'Edit Listing'}</h3>
              <button onClick={() => { setOverlay(null); setEditListing(null); }} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 text-gray-600">✕</button>
            </div>
            <div className="p-5">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 text-center">
                <ItemImage url={editListing.item_image} size="lg" className="mx-auto mb-2" />
                <p className="text-sm font-bold text-gray-800">{editListing.item_name}</p>
              </div>

              <div className="mb-4">
                <label className="text-xs font-bold text-gray-600 block mb-2">{s.mp_how_to_trade || 'How do you want to trade?'}</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-[#D4A843] transition-colors cursor-pointer">
                    <input type="checkbox" checked={editAcceptItems} onChange={e => setEditAcceptItems(e.target.checked)} className="w-4 h-4 rounded text-[#2D6A4F] focus:ring-[#2D6A4F]" />
                    <div>
                      <p className="text-xs font-bold text-gray-700">{s.mp_accept_items_label || '🤝 Accept item trades'}</p>
                    </div>
                  </label>
                  <div className="p-3 rounded-xl border-2 border-gray-200">
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{iconToken}</span>
                      <div className="flex-1">
                        <p className="text-xs font-bold text-gray-700">{s.mp_token_price_label || 'Set token price (optional)'}</p>
                        <input type="number" value={editTokenPrice} onChange={e => setEditTokenPrice(e.target.value)} placeholder="0" min="0" className="w-full mt-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-[#D4A843] focus:outline-none" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs font-bold text-gray-600 block mb-1.5">{s.mp_want_label || 'What do you want in return? (optional)'}</label>
                <input
                  type="text" value={editWantedDesc} onChange={e => setEditWantedDesc(e.target.value)}
                  placeholder={s.mp_want_placeholder || 'e.g. jacket, shoes, accessories...'}
                  className={`w-full px-3 py-2.5 rounded-xl border-2 text-sm focus:outline-none transition-colors ${hasContactInfo(editWantedDesc) ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-[#D4A843]'}`}
                  maxLength={100}
                />
                {hasContactInfo(editWantedDesc) && (
                  <p className="text-[10px] text-red-500 font-semibold mt-1">{s.mp_no_contact_info || '⚠️ No emails, phone numbers, or social media allowed — this is a game!'}</p>
                )}
              </div>

              <div className="space-y-2.5">
                <button
                  onClick={handleEditListing}
                  disabled={editPending || hasContactInfo(editWantedDesc) || (!editAcceptItems && (parseInt(editTokenPrice) || 0) <= 0)}
                  className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                >
                  {editPending ? (s.mp_saving || 'Saving...') : (s.mp_save_changes || 'Save Changes')}
                </button>
                <button onClick={() => { setOverlay(null); setEditListing(null); }} className="w-full py-2.5 text-gray-400 text-sm hover:text-gray-600 transition-colors">
                  {s.mp_cancel_button || 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confetti */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          {Array.from({ length: 30 }, (_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-5%`,
                fontSize: `${12 + Math.random() * 14}px`,
                animation: `fall ${1.5 + Math.random() * 2}s ease-in forwards`,
                animationDelay: `${Math.random() * 0.5}s`,
              }}
            >
              {['🎉', '✨', '🌟', '🎊', '🤝', '👗', '🧥'][Math.floor(Math.random() * 7)]}
            </div>
          ))}
          <style>{`
            @keyframes fall {
              0% { transform: translateY(0) rotate(0deg); opacity: 1; }
              100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
